import React from 'react';
import '../assets/FleetAnalytics.css';
import CompactPieChart from '../reports/CompactPieChart';
import HistogramChart from '../reports/HistogramChart';
import BootKpiTable from '../reports/BootKpiTable';
import { LoadingSkeleton } from '../reports/LoadingSkeleton';
import deviceDataService from '../../services/deviceDataService';
import RawAnalyticsPanels from '../reports/RawAnalyticsPanels';
import CpuHealthSection from './CpuHealthSection';
import MemoryHealthSection from './MemoryHealthSection';
import ThermalHealthSection from './ThermalHealthSection';
import FlashHealthSection from './FlashHealthSection';
import ThroughputStatsSection from '../reports/ThroughputStatsSection';
import { fetchFleetSummary } from '../api/restapi';

// Fleet-health domain metadata
const DOMAIN_CONFIG = {
    BOOT:              { label: 'Boot KPIs',              unit: 's',     precision: 2 },
    REGISTRATION:      { label: 'Registration Failures',  unit: '%',     precision: 1 },
    PDN:               { label: 'PDN Failures',           unit: '%',     precision: 1 },
    MEMORY:            { label: 'Memory / Flash Usage',   unit: '%',     precision: 1 },
    DATASTALL:         { label: 'Data Stalls',            unit: '',      precision: 0 },
    SUBSYSTEM_RESTART: { label: 'MODEM Restarts',         unit: '',      precision: 0 },
    UPLINK:            { label: 'Uplink Throughput',      unit: ' Mbps', precision: 1 },
    DOWNLINK:          { label: 'Downlink Throughput',    unit: ' Mbps', precision: 1 },
};

function statusToAccent(status) {
    const s = (status || '').toLowerCase();
    if (s === 'critical' || s === 'error' || s === 'bad') return { icon: '🔴', sevClass: 'sev-red', badgeClass: 'badge-red' };
    if (s === 'warning' || s === 'warn') return { icon: '🟠', sevClass: 'sev-amber', badgeClass: 'badge-amber' };
    if (s === 'ok' || s === 'good' || s === 'healthy') return { icon: '🟢', sevClass: 'sev-green', badgeClass: 'badge-green' };
    return { icon: '⚪', sevClass: 'sev-gray', badgeClass: 'badge-gray' };
}

const DURATIONS = [
    { key: '1d',  label: 'D' },
    { key: '7d',  label: 'W' },
    { key: '30d', label: 'M' },
];

function SectionHeader({ icon, tone = 'blue', title, subtitle, first = false }) {
    return (
        <div className="sec-hdr" style={first ? { marginTop: 0 } : undefined}>
            <div className={`sec-icon ${tone}`}>{icon}</div>
            <h2>{title}</h2>
            {subtitle && <span className="sec-sub">{subtitle}</span>}
            <div className="line" />
        </div>
    );
}

function CollapsibleSection({ icon, tone = 'blue', title, subtitle, helpText, first = false, children, defaultExpanded = false }) {
    const [expanded, setExpanded] = React.useState(defaultExpanded);
    return (
        <div className={`sec-collapsible${first ? ' sec-collapsible-first' : ''}`}>
            <div className="sec-hdr sec-hdr-clickable" onClick={() => setExpanded(e => !e)}>
                <div className={`sec-icon ${tone}`}>{icon}</div>
                <h2>{title}</h2>
                {helpText && <button type="button" className="section-help" aria-label={`${title} information`} title={helpText} onClick={(event) => event.stopPropagation()}>i</button>}
                {subtitle && <span className="sec-sub">{subtitle}</span>}
                <div className="line" />
                <span className={`sec-chevron${expanded ? ' expanded' : ''}`}>&#8250;</span>
            </div>
            {expanded && <div className="sec-body">{children}</div>}
        </div>
    );
}

function FleetOverviewCards({ summary, loading }) {
    const onboarded = summary?.devices_onboarded ?? 0;
    const telemetry = summary?.devices_uploading_crowdsourcing_data ?? 0;
    const diagnostics = summary?.devices_registered_diagnostics ?? 0;
    if (loading) return <div className="grid-4"><LoadingSkeleton height="96px" /><LoadingSkeleton height="96px" /><LoadingSkeleton height="96px" /></div>;
    return <div className="grid-4" style={{ marginBottom: 20 }}>
        <div className="kpi-card"><div className="kpi-label">Devices Onboarded</div><div className="kpi-value">{onboarded.toLocaleString()}</div><div className="kpi-sub">As of the selected period end</div></div>
        <div className="kpi-card"><div className="kpi-label">Uploading Telemetry Data</div><div className="kpi-value">{telemetry.toLocaleString()}</div><div className="kpi-sub">Distinct devices in this duration</div></div>
        <div className="kpi-card"><div className="kpi-label">Registered for Diagnostics</div><div className="kpi-value">{diagnostics.toLocaleString()}</div><div className="kpi-sub">Distinct devices in this duration</div></div>
    </div>;
}

function DomainCard({ domain, expandedId, onToggle, onDeviceClick }) {
    const config = DOMAIN_CONFIG[domain.domain] || { label: domain.displayName, unit: '', precision: 1 };
    const devices = domain.devices || [];
    const { icon, sevClass, badgeClass } = statusToAccent(domain.status);
    const isOpen = expandedId === domain.domain;

    return (
        <div className={`cluster-card${isOpen ? ' open' : ''}`}>
            <div className="cluster-hdr" onClick={() => onToggle(domain.domain)}>
                <div className={`cluster-sev ${sevClass}`}>{icon}</div>
                <div style={{ flex: 1 }}>
                    <div className="cluster-title">
                        {config.label}
                        <span className={`badge ${badgeClass}`}>
                            {domain.flaggedCount} device{domain.flaggedCount !== 1 ? 's' : ''} flagged
                        </span>
                        {domain.status && (
                            <span className="badge badge-gray">{domain.status}</span>
                        )}
                    </div>
                    <div className="cluster-desc">
                        {devices.length === 0
                            ? 'No flagged devices for this domain'
                            : `${devices.length} device${devices.length !== 1 ? 's' : ''} with reported issues`}
                    </div>
                    {domain.fromDate && (
                        <div className="cluster-meta">
                            <span className="m">Window: <b>{domain.fromDate} → {domain.toDate}</b></span>
                        </div>
                    )}
                </div>
                <div className="cluster-expand-icon">▼</div>
            </div>

            <div className={`cluster-body${isOpen ? ' open' : ''}`}>
                {devices.length === 0 ? (
                    <div style={{ color: 'var(--fa-text-muted)', fontSize: 13 }}>No flagged devices.</div>
                ) : (
                    <div className="dtable-wrap dtable-scroll">
                        <table className="dtable" style={{ tableLayout: 'fixed', width: '100%' }}>
                            <colgroup>
                                <col style={{ width: '60%' }} />
                                <col style={{ width: '20%' }} />
                                <col style={{ width: '20%' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>System ID</th>
                                    <th>Value</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {devices.map((d) => (
                                    <tr key={d.deviceId}>
                                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.deviceId}>{d.deviceId}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            {typeof d.metricValue === 'number'
                                                ? d.metricValue.toFixed(config.precision)
                                                : d.metricValue}
                                            {config.unit}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <span
                                                className="dlink"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => onDeviceClick && onDeviceClick(d.deviceId)}
                                            >
                                                Deep Dive →
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function NetworkIdentifierCharts({ distributions = {} }) {
    const help = (identifier, extra = '') =>
        `The X-axis lists the top 20 raw ${identifier} values reported during the selected duration. ` +
        `Each bar is the number of unique devices reporting that value; one device can appear in multiple bars if it reported multiple values. ${extra}`;

    return (
        <div className="fa-histogram-grid fa-histogram-grid--identifiers">
            <HistogramChart title="PCI Histogram Counts" bins={distributions.pci?.bins || []} xAxisLabel="PCI value" yAxisLabel="Unique devices" color="#8e44ad" infoText={help('PCI')} />
            <HistogramChart title="EARFCN Histogram Counts" bins={distributions.earfcn?.bins || []} xAxisLabel="EARFCN value" yAxisLabel="Unique devices" color="#16a085" infoText={help('EARFCN')} />
            <HistogramChart title="ARFCN Histogram Counts" datasets={distributions.arfcn?.datasets || []} xAxisLabel="ARFCN value" yAxisLabel="Unique devices" showLegend infoText={help('ARFCN', 'Colors separate LTE, NR, and Other/Unknown reports.')} />
            <HistogramChart title="CGI Histogram Counts" bins={distributions.cgi?.bins || []} xAxisLabel="CGI value" yAxisLabel="Unique devices" color="#d35400" infoText={help('CGI')} />
            <HistogramChart title="MNC/MCC Pair Histogram Counts" bins={distributions.mncMcc?.bins || []} xAxisLabel="MNC-MCC pair" yAxisLabel="Unique devices" color="#c0392b" infoText={help('MNC/MCC pair')} />
        </div>
    );
}

// ── Main component ──
function FleetAnalyticsDashboard({ onDeviceClick }) {

    // ── View type + tag ──
    const [viewType, setViewType] = React.useState('aggregate'); // 'aggregate' | 'device-tag'
    const [selectedTag, setSelectedTag] = React.useState(null);
    const [availableTags, setAvailableTags] = React.useState([]);
    const [tagsLoading, setTagsLoading] = React.useState(false);

    // ── Duration ──
    const [selectedTimeRange, setSelectedTimeRange] = React.useState('1d');

    // ── Aggregate / DeviceTag dashboard data ──
    const [data, setData] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [fleetHealth, setFleetHealth] = React.useState({ data: null, loading: false, error: null });
    const [expandedDomain, setExpandedDomain] = React.useState(null);
    const [throughput, setThroughput] = React.useState({ data: null, loading: false, error: null });
    const [throughputThreshold, setThroughputThreshold] = React.useState(null);
    const [fleetSummary, setFleetSummary] = React.useState({ data: null, loading: false });

    // ── Aggregate dashboard data loaders ──
    const loadAggregateData = React.useCallback(async (timeRange) => {
        try {
            setLoading(true); setError(null);
            const d = await deviceDataService.getAggregateDashboard(timeRange);
            if (d?.cacheError) { setData(null); setError({ type: 'cache_error', message: d.message, details: d.details, timeRange: d.timeRange }); }
            else setData(d);
        } catch (err) {
            setError({ type: 'network_error', message: err.message, details: 'An unexpected error occurred.' });
        } finally { setLoading(false); }
    }, []);

    const fetchFleetHealth = React.useCallback(async (timeRange) => {
        try {
            setFleetHealth(prev => ({ ...prev, loading: true, error: null }));
            const result = await deviceDataService.getFleetHealth(timeRange);
            if (result?.cacheError) setFleetHealth({ data: null, loading: false, error: { message: result.message } });
            else setFleetHealth({ data: result, loading: false, error: null });
        } catch (err) {
            setFleetHealth({ data: null, loading: false, error: { message: err.message } });
        }
    }, []);

    const fetchThroughput = React.useCallback(async (timeRange, tag = null, threshold = null) => {
        try {
            setThroughput(prev => ({ ...prev, loading: true, error: null }));
            const result = await deviceDataService.getThroughputStats(timeRange, tag, threshold);
            setThroughput({ data: result, loading: false, error: null });
        } catch (err) {
            setThroughput({ data: null, loading: false, error: { message: err.message } });
        }
    }, []);

    const fetchAnalyticsSummary = React.useCallback(async (timeRange) => {
        const duration = ({ '1d': '1D', '7d': '7D', '30d': '1M' })[timeRange] || '7D';
        setFleetSummary((current) => ({ ...current, loading: true }));
        const result = await fetchFleetSummary(duration);
        setFleetSummary({ data: result?.data?.fleet_stats || result?.fleet_stats || null, loading: false });
    }, []);

    // ── Device Tag dashboard data ──
    const [tagData, setTagData] = React.useState(null);
    const [tagLoading, setTagLoading] = React.useState(false);
    const [tagError, setTagError] = React.useState(null);

    const loadTagData = React.useCallback(async (tag, timeRange) => {
        if (!tag) return;
        try {
            setTagLoading(true); setTagError(null);
            const d = await deviceDataService.getDeviceTagDashboard(tag, timeRange);
            if (d?.cacheError) { setTagData(null); setTagError({ message: d.message }); }
            else setTagData(d);
        } catch (err) {
            setTagError({ message: err.message });
        } finally { setTagLoading(false); }
    }, []);

    // ── Load available tags when switching to device-tag view ──
    React.useEffect(() => {
        if (viewType !== 'device-tag' || availableTags.length > 0) return;
        const apiBase = (process.env.REACT_APP_WBC_API_BASE_URL || '').replace(/\/$/, '');
        const qpath = (process.env.REACT_APP_WBC_QUERY_SERVICE_PATH || '/queryService/api/v1');
        setTagsLoading(true);
        fetch(`${apiBase}${qpath.startsWith('/') ? qpath : '/' + qpath}/aggregates/tags`)
            .then(r => r.json())
            .then(d => setAvailableTags(d.tags || []))
            .catch(() => setAvailableTags([]))
            .finally(() => setTagsLoading(false));
    }, [viewType, availableTags.length]);

    // ── Main effect: re-fetch dashboard data on duration or viewType/tag change ──
    React.useEffect(() => {
        if (viewType === 'aggregate') {
            loadAggregateData(selectedTimeRange);
            fetchFleetHealth(selectedTimeRange);
            fetchThroughput(selectedTimeRange, null, throughputThreshold);
        } else {
            // Device Tag view — only fetch tag dashboard if a tag is selected
            if (selectedTag) {
                loadTagData(selectedTag, selectedTimeRange);
                fetchThroughput(selectedTimeRange, selectedTag, throughputThreshold);
            } else {
                setThroughput({ data: null, loading: false, error: null });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        fetchAnalyticsSummary(selectedTimeRange);
    }, [selectedTimeRange, viewType, selectedTag, throughputThreshold, fetchAnalyticsSummary]);

    const handleTimeRangeChange = (key) => {
        if (DURATIONS.find(d => d.key === key)?.disabled) return;
        setSelectedTimeRange(key);
    };

    const handleViewTypeChange = (vt) => {
        if (vt === viewType) return;
        setViewType(vt);
    };

    const handleTagChange = (tag) => {
        setSelectedTag(tag || null);
    };

    // ── Derived KPI values (aggregate view) ──
    const domains = fleetHealth.data?.domains || [];
    const handleDomainToggle = (k) => setExpandedDomain(prev => prev === k ? null : k);

    // ── Control bar ──
    const controls = (
        <div className="page-intro">
            <div className="duration-bar">
                <span className="duration-label">Duration:</span>
                {DURATIONS.map(d => (
                    <span
                        key={d.key}
                        className={`filter-chip${selectedTimeRange === d.key ? ' active' : ''}${d.disabled ? ' disabled' : ''}`}
                        onClick={() => handleTimeRangeChange(d.key)}
                        title={d.disabled ? 'Not available for AI Insights' : undefined}
                    >
                        {d.label}
                    </span>
                ))}
                <span className="duration-divider" />
                <span className="duration-label">View:</span>
                <span
                    className={`filter-chip${viewType === 'aggregate' ? ' active' : ''}`}
                    onClick={() => handleViewTypeChange('aggregate')}
                >
                    Aggregate
                </span>
                <span
                    className={`filter-chip${viewType === 'device-tag' ? ' active' : ''}`}
                    onClick={() => handleViewTypeChange('device-tag')}
                >
                    Device Tag
                </span>
            </div>
        </div>
    );

    // ── Device Tag view ──
    if (viewType === 'device-tag') {
        const nh = tagData?.networkHealth || {};
        const bk = tagData?.bootKpi || {};
        return (
            <div className="fho-dashboard">
                {controls}

                <CollapsibleSection first icon="📡" tone="blue" title="Fleet Overview" defaultExpanded>
                    <FleetOverviewCards summary={fleetSummary.data} loading={fleetSummary.loading} />
                </CollapsibleSection>

                {/* Tag selector */}
                <div className="tag-selector-row">
                    <span className="duration-label" style={{ marginRight: 8 }}>Device Group:</span>
                    <select
                        className="tag-select"
                        value={selectedTag || ''}
                        onChange={e => handleTagChange(e.target.value)}
                        disabled={tagsLoading}
                    >
                        <option value="">{tagsLoading ? 'Loading groups…' : availableTags.length === 0 ? 'No device groups found' : 'Select a device group…'}</option>
                        {availableTags.map(tag => (
                            <option key={tag} value={tag}>{tag}</option>
                        ))}
                    </select>
                </div>

                {!selectedTag ? (
                    <div className="tag-empty-state">
                        <div className="tag-empty-icon">🏷️</div>
                        <div className="tag-empty-text">Select a device group to load insights</div>
                    </div>
                ) : (
                    <>
                        {/* Network Health */}
                        <CollapsibleSection first icon="📶" tone="blue" title="Network Health">
                        {tagLoading ? (
                            <div className="card fa-card-body"><LoadingSkeleton height="260px" /></div>
                        ) : tagError ? (
                            <div className="card fa-card-body"><div className="fa-state"><div className="fa-state-icon">📭</div><h3>Data Not Available</h3><p>{tagError.message}</p></div></div>
                        ) : (
                            <>
                                <div className="fa-network-health-pie-rows">
                                    <div className="fa-pie-grid fa-pie-grid--two">
                                        <CompactPieChart title="NAS Registration Status" data={nh.registrationStatus?.data || []} labels={nh.registrationStatus?.labels || []} colors={nh.registrationStatus?.colors || []} />
                                        <CompactPieChart title="NAS Registration Failure Reasons" data={nh.registrationFailures?.data || []} labels={nh.registrationFailures?.labels || []} colors={nh.registrationFailures?.colors || []} />
                                    </div>
                                    <div className="fa-pie-grid fa-pie-grid--three">
                                        <CompactPieChart title="PDN Status" data={nh.pdnStatus?.data || []} labels={nh.pdnStatus?.labels || []} colors={nh.pdnStatus?.colors || []} />
                                        <CompactPieChart title="PDN Failure Reasons" data={nh.pdnFailureReasons?.data || []} labels={nh.pdnFailureReasons?.labels || []} colors={nh.pdnFailureReasons?.colors || []} />
                                        <CompactPieChart title="PDN APN Type Distribution" data={nh.pdnApnTypeDistribution?.data || []} labels={nh.pdnApnTypeDistribution?.labels || []} colors={nh.pdnApnTypeDistribution?.colors || []} />
                                    </div>
                                </div>
                                <div className="fa-histogram-grid">
                                    <HistogramChart title="Signal Strength (RSRP)" bins={nh.rsrpHistogram?.bins || []} xAxisLabel="RSRP (dBm)" yAxisLabel="Number of Instances" color="#3498db" />
                                    <HistogramChart title="Signal Strength (RSRQ)" bins={nh.rsrqHistogram?.bins || []} xAxisLabel="RSRQ (dB)" yAxisLabel="Number of Instances" color="#e74c3c" />
                                    <HistogramChart title="Signal Strength (SINR)" bins={nh.sinrHistogram?.bins || []} xAxisLabel="SINR (dB)" yAxisLabel="Number of Instances" color="#2ecc71" />
                                </div>
                                <NetworkIdentifierCharts distributions={nh.identifierDistributions} />
                            </>
                        )}
                        </CollapsibleSection>

                        <CollapsibleSection icon="⏸️" tone="orange" title="Data Stall Events">
                        <RawAnalyticsPanels
                            domain="stall"
                            timeRange={selectedTimeRange}
                            viewType="device-tag"
                            selectedTag={selectedTag}
                            onEventClick={(event) => onDeviceClick && onDeviceClick({
                                systemId: event.systemId,
                                referenceTimestamp: event.timestamp,
                            })}
                        />
                        </CollapsibleSection>

                        <CollapsibleSection icon="🔁" tone="purple" title="Modem Subsystem Events">
                        <RawAnalyticsPanels
                            domain="ssr"
                            timeRange={selectedTimeRange}
                            viewType="device-tag"
                            selectedTag={selectedTag}
                            onEventClick={(event) => onDeviceClick && onDeviceClick({
                                systemId: event.systemId,
                                referenceTimestamp: event.timestamp,
                            })}
                        />
                        </CollapsibleSection>

                        <CollapsibleSection icon="📶" tone="blue" title="Throughput">
                            <ThroughputStatsSection
                                data={throughput.data}
                                loading={throughput.loading}
                                error={throughput.error}
                                timeRange={selectedTimeRange}
                                analyticsStyle
                                suppressAnalyticsHeader
                                onDeviceClick={onDeviceClick}
                                onThresholdApply={setThroughputThreshold}
                            />
                        </CollapsibleSection>

                        {/* Boot KPI */}
                        <CollapsibleSection icon="⏱️" tone="orange" title="Boot KPI">
                        {tagLoading ? (
                            <div className="card fa-card-body"><LoadingSkeleton height="160px" /></div>
                        ) : !tagError && (
                            <>
                                <div style={{ maxWidth: 420 }}>
                                    <CompactPieChart title="Boot KPI Type Distribution" data={bk.kpiDistribution?.data || []} labels={bk.kpiDistribution?.labels || []} colors={bk.kpiDistribution?.colors || []} />
                                </div>
                                <div style={{ marginTop: 18, overflowX: 'auto' }}>
                                    <BootKpiTable timingHistograms={bk.timingHistograms || {}} />
                                </div>
                            </>
                        )}
                        </CollapsibleSection>

                        {/* ── CPU Health ── */}
                        <CollapsibleSection icon="🧠" tone="purple" title="CPU Health">
                            <CpuHealthSection
                                timeRange={selectedTimeRange}
                                deviceTag={selectedTag}
                                onDeviceClick={onDeviceClick}
                            />
                        </CollapsibleSection>
                        <CollapsibleSection icon="💾" tone="purple" title="Memory Health" helpText="Total capacity is the latest telemetry value reported within the selected range. If capacity changed during the range, the displayed average used percentage uses that latest value.">
                            <MemoryHealthSection
                                timeRange={selectedTimeRange}
                                deviceTag={selectedTag}
                                onDeviceClick={onDeviceClick}
                            />
                        </CollapsibleSection>
                        <CollapsibleSection icon="🌡️" tone="purple" title="Thermal Health">
                            <ThermalHealthSection
                                timeRange={selectedTimeRange}
                                deviceTag={selectedTag}
                                onDeviceClick={onDeviceClick}
                            />
                        </CollapsibleSection>
                        <CollapsibleSection icon="💽" tone="purple" title="Flash Health" helpText="Total capacity is the latest telemetry value reported within the selected range. If capacity changed during the range, the displayed average used percentage uses that latest value.">
                            <FlashHealthSection
                                timeRange={selectedTimeRange}
                                deviceTag={selectedTag}
                                onDeviceClick={onDeviceClick}
                            />
                        </CollapsibleSection>
                    </>
                )}
            </div>
        );
    }

    // ── Aggregate view ──
    if (loading && !data) {
        return (
            <div className="fho-dashboard">
                {controls}
                <SectionHeader first icon="📡" tone="blue" title="Fleet Overview" />
                <div className="grid-4" style={{ marginBottom: 20 }}>
                    <LoadingSkeleton height="96px" /><LoadingSkeleton height="96px" />
                    <LoadingSkeleton height="96px" /><LoadingSkeleton height="96px" />
                </div>
                <SectionHeader icon="📶" tone="blue" title="Network Health" />
                <div className="card fa-card-body"><LoadingSkeleton height="260px" /></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fho-dashboard">
                {controls}
                <SectionHeader first icon="📡" tone="blue" title="Fleet Overview" />
                <div className="card fa-card-body">
                    <div className="fa-state">
                        <div className="fa-state-icon">📭</div>
                        <h3>Data Not Available</h3>
                        <p>{error.message || 'Unable to load aggregate data.'}</p>
                        {error.details && <p style={{ color: 'var(--fa-text-muted)', fontSize: 13 }}>{error.details}</p>}
                        <div className="fa-retry-row">
                            {DURATIONS.filter(d => !d.disabled).map(d => (
                                <button key={d.key} className={`fa-retry-btn${selectedTimeRange === d.key ? ' active' : ''}`} onClick={() => handleTimeRangeChange(d.key)}>{d.label}</button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const nh = data?.networkHealth || {};
    const bk = data?.bootKpi || {};

    return (
        <div className="fho-dashboard">
            {controls}

            {/* ── Fleet Overview KPIs ── */}
            <CollapsibleSection first icon="📡" tone="blue" title="Fleet Overview" defaultExpanded>
                <FleetOverviewCards summary={fleetSummary.data} loading={fleetSummary.loading} />
            </CollapsibleSection>

            {/* ── Fleet Health domains (hidden — set to false to re-enable) ── */}
            {false && (
                <>
                    <SectionHeader icon="🩺" tone="green" title="Fleet Health" />
                    {fleetHealth.loading ? (
                        <div className="card fa-card-body"><LoadingSkeleton height="160px" /></div>
                    ) : fleetHealth.error ? (
                        <div className="card fa-card-body"><div className="fa-state">{fleetHealth.error.message || 'Fleet health data unavailable.'}</div></div>
                    ) : domains.length === 0 ? (
                        <div className="card fa-card-body"><div className="fa-state">No fleet health data available.</div></div>
                    ) : (
                        <>
                            {fleetHealth.data?.fromDate && (
                                <div style={{ fontSize: 12, color: 'var(--fa-text-muted)', marginBottom: 12 }}>
                                    Window: {fleetHealth.data.fromDate} → {fleetHealth.data.toDate}
                                    {fleetHealth.data.tag && fleetHealth.data.tag !== 'all' && ` · Group: ${fleetHealth.data.tag}`}
                                </div>
                            )}
                            <div className="grid-2">
                                {domains.map(domain => (
                                    <DomainCard key={domain.domain} domain={domain} expandedId={expandedDomain} onToggle={handleDomainToggle} onDeviceClick={onDeviceClick} />
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}

            {/* ── Network Health ── */}
            <CollapsibleSection icon="📶" tone="blue" title="Network Health">
                {loading ? (
                    <div className="card fa-card-body"><LoadingSkeleton height="260px" /></div>
                ) : (
                <>
                <div className="fa-network-health-pie-rows">
                    <div className="fa-pie-grid fa-pie-grid--two">
                        <CompactPieChart title="NAS Registration Status" data={nh.registrationStatus?.data || []} labels={nh.registrationStatus?.labels || []} colors={nh.registrationStatus?.colors || []} />
                        <CompactPieChart title="NAS Registration Failure Reasons" data={nh.registrationFailures?.data || []} labels={nh.registrationFailures?.labels || []} colors={nh.registrationFailures?.colors || []} />
                    </div>
                    <div className="fa-pie-grid fa-pie-grid--three">
                        <CompactPieChart title="PDN Status" data={nh.pdnStatus?.data || []} labels={nh.pdnStatus?.labels || []} colors={nh.pdnStatus?.colors || []} />
                        <CompactPieChart title="PDN Failure Reasons" data={nh.pdnFailureReasons?.data || []} labels={nh.pdnFailureReasons?.labels || []} colors={nh.pdnFailureReasons?.colors || []} />
                        <CompactPieChart title="PDN APN Type Distribution" data={nh.pdnApnTypeDistribution?.data || []} labels={nh.pdnApnTypeDistribution?.labels || []} colors={nh.pdnApnTypeDistribution?.colors || []} />
                    </div>
                </div>
                <div className="fa-histogram-grid">
                    <HistogramChart title="Signal Strength (RSRP)" bins={nh.rsrpHistogram?.bins || []} xAxisLabel="RSRP (dBm)" yAxisLabel="Number of Instances" color="#3498db" />
                    <HistogramChart title="Signal Strength (RSRQ)" bins={nh.rsrqHistogram?.bins || []} xAxisLabel="RSRQ (dB)" yAxisLabel="Number of Instances" color="#e74c3c" />
                    <HistogramChart title="Signal Strength (SINR)" bins={nh.sinrHistogram?.bins || []} xAxisLabel="SINR (dB)" yAxisLabel="Number of Instances" color="#2ecc71" />
                </div>
                <NetworkIdentifierCharts distributions={nh.identifierDistributions} />
                </>
                )}
            </CollapsibleSection>

            <CollapsibleSection icon="⏸️" tone="orange" title="Data Stall Events">
                <RawAnalyticsPanels
                    domain="stall"
                    timeRange={selectedTimeRange}
                    viewType="aggregate"
                    selectedTag={null}
                    onEventClick={(event) => onDeviceClick && onDeviceClick({
                        systemId: event.systemId,
                        referenceTimestamp: event.timestamp,
                        timeRange: selectedTimeRange,
                    })}
                />
            </CollapsibleSection>

            <CollapsibleSection icon="🔁" tone="purple" title="Modem Subsystem Events">
                <RawAnalyticsPanels
                    domain="ssr"
                    timeRange={selectedTimeRange}
                    viewType="aggregate"
                    selectedTag={null}
                    onEventClick={(event) => onDeviceClick && onDeviceClick({
                        systemId: event.systemId,
                        referenceTimestamp: event.timestamp,
                        timeRange: selectedTimeRange,
                    })}
                />
            </CollapsibleSection>

            <CollapsibleSection icon="📶" tone="blue" title="Throughput">
                <ThroughputStatsSection
                    data={throughput.data}
                    loading={throughput.loading}
                    error={throughput.error}
                    timeRange={selectedTimeRange}
                    analyticsStyle
                    suppressAnalyticsHeader
                    onDeviceClick={onDeviceClick}
                    onThresholdApply={setThroughputThreshold}
                />
            </CollapsibleSection>

            {/* ── Boot KPI ── */}
            <CollapsibleSection icon="⏱️" tone="orange" title="Boot KPI">
                {loading ? (
                    <div className="card fa-card-body"><LoadingSkeleton height="160px" /></div>
                ) : (
                <>
                <div style={{ maxWidth: 420 }}>
                    <CompactPieChart title="Boot KPI Type Distribution" data={bk.kpiDistribution?.data || []} labels={bk.kpiDistribution?.labels || []} colors={bk.kpiDistribution?.colors || []} />
                </div>
                <div style={{ marginTop: 18, overflowX: 'auto' }}>
                    <BootKpiTable timingHistograms={bk.timingHistograms || {}} />
                </div>
                </>
                )}
            </CollapsibleSection>

            {/* ── CPU Health ── */}
            <CollapsibleSection icon="🧠" tone="purple" title="CPU Health">
                <CpuHealthSection
                    timeRange={selectedTimeRange}
                    onDeviceClick={onDeviceClick}
                />
            </CollapsibleSection>
            <CollapsibleSection icon="💾" tone="purple" title="Memory Health" helpText="Total capacity is the latest telemetry value reported within the selected range. If capacity changed during the range, the displayed average used percentage uses that latest value.">
                <MemoryHealthSection
                    timeRange={selectedTimeRange}
                    onDeviceClick={onDeviceClick}
                />
            </CollapsibleSection>
            <CollapsibleSection icon="🌡️" tone="purple" title="Thermal Health">
                <ThermalHealthSection
                    timeRange={selectedTimeRange}
                    onDeviceClick={onDeviceClick}
                />
            </CollapsibleSection>
            <CollapsibleSection icon="💽" tone="purple" title="Flash Health" helpText="Total capacity is the latest telemetry value reported within the selected range. If capacity changed during the range, the displayed average used percentage uses that latest value.">
                <FlashHealthSection
                    timeRange={selectedTimeRange}
                    onDeviceClick={onDeviceClick}
                />
            </CollapsibleSection>
        </div>
    );
}

export default FleetAnalyticsDashboard;
